import { useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext.tsx';
import { useLoyalty } from '../contexts/LoyaltyContext.tsx';
import { resolvePropsDeep } from '../lib/templateResolver.ts';

/**
 * Builds a template context from the current customer + loyalty data
 * and exposes a resolver function for block props.
 *
 * For unauthenticated users, customer/loyalty values are empty strings/zeros.
 */
export function useVariableContext() {
  const { user } = useAuth();
  const { data: loyalty } = useLoyalty();

  const context = useMemo<Record<string, unknown>>(() => {
    const profile = loyalty?.profile;
    return {
      customer: {
        firstName: user?.firstName ?? profile?.firstName ?? '',
        lastName: user?.lastName ?? profile?.lastName ?? '',
        fullName:
          [user?.firstName ?? profile?.firstName, user?.lastName ?? profile?.lastName]
            .filter(Boolean)
            .join(' ') || '',
        email: user?.email ?? '',
      },
      loyalty: {
        pointsBalance: profile?.pointsBalance ?? 0,
        tierCode: profile?.tierCode ?? '',
        tierName: profile?.tierName ?? '',
        lifetimePoints: profile?.lifetimePoints ?? 0,
      },
    };
  }, [user, loyalty]);

  /** Resolve all {{...}} variables in a block's props */
  function resolveBlockProps(props: Record<string, unknown>): Record<string, unknown> {
    return resolvePropsDeep(props, context);
  }

  return { context, resolveBlockProps };
}
